<script setup lang="ts">
import { nextTick, ref } from 'vue'
import { NCard, NForm, NFormItem, NInput, NButton, NSpace } from 'naive-ui'
import { useSessionStore } from '../stores/session'

const session = useSessionStore()

const email = ref('')
const password = ref('')
const error = ref<string | null>(null)
const busy = ref(false)
const passwordRef = ref<InstanceType<typeof NInput> | null>(null)

async function submit() {
  if (busy.value) return
  busy.value = true
  error.value = null
  try {
    await session.signIn(email.value, password.value)
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'sign-in failed'
  } finally {
    busy.value = false
  }
}

// Tab-from-email-to-password: iOS soft keyboards show the
// `enterkeyhint` glyph on the return key. With `next` the user gets a
// `Next` glyph that submits the form prematurely without focus
// advancement; we honour the hint by moving focus to the password
// field on Enter so the keyboard reads as Next → Done across both
// fields. Desktop browsers ignore enterkeyhint but get the same
// focus-advance behaviour, which matches what password managers
// expect.
async function advanceToPassword(event: KeyboardEvent) {
  if (event.key !== 'Enter') return
  event.preventDefault()
  await nextTick()
  passwordRef.value?.focus?.()
}
</script>

<template>
  <section class="max-w-md mx-auto mt-8 sm:mt-16 px-4">
    <div class="text-center mb-6">
      <h1 class="text-3xl font-bold text-white m-0">superbrain</h1>
      <p class="text-white/40 mt-1">Your personal LLM-maintained knowledge brain</p>
    </div>

    <NCard>
      <NForm @submit.prevent="submit">
        <NFormItem label="Email">
          <NInput
            v-model:value="email"
            placeholder="you@example.com"
            :input-props="{ autocomplete: 'email', type: 'email', enterkeyhint: 'next' }"
            autofocus
            @keydown="advanceToPassword"
          />
        </NFormItem>

        <NFormItem label="Password" :feedback="error ?? undefined" :validation-status="error ? 'error' : undefined">
          <NInput
            ref="passwordRef"
            v-model:value="password"
            type="password"
            :input-props="{ autocomplete: 'current-password', enterkeyhint: 'done' }"
            show-password-on="click"
          />
        </NFormItem>

        <NSpace justify="end">
          <NButton type="primary" attr-type="submit" :loading="busy" :disabled="!email || !password"> Sign in </NButton>
        </NSpace>
      </NForm>

      <div class="flex items-center gap-3 my-4 text-white/30 text-sm">
        <div class="flex-1 h-px bg-white/10" />
        or
        <div class="flex-1 h-px bg-white/10" />
      </div>

      <NButton tag="a" href="/oauth/discord" block> Sign in with Discord </NButton>
    </NCard>
  </section>
</template>
